import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useReviewQueueQuery, useReviewItemQuery, useClassifiedMessagesQuery,
  useMessageViewQuery, useSaveLabelFeedbackMutation,
  useTagMappingsQuery,
  useApproveReviewMutation, useRejectReviewMutation, useNoActionReviewMutation,
  useEditReviewTagsMutation, useCorrectRelationshipMutation,
  type ReviewItem,
} from './api.js';
import { Alert, Button, Card, EmptyState, Spinner, SectionHeading, formatWhen } from './ui.js';

/**
 * Email Intelligence.
 *
 * This screen exists for two reasons, and the second one is not obvious.
 *
 * The first is the ordinary one: somebody has to decide what a message means for the
 * CRM, and a queue is how they do it.
 *
 * The second is compliance. Google permits transferring mail to a processor only "to
 * provide or improve your appropriate access or user-facing features that are visible
 * and prominent in the requesting application's user interface". A classifier that
 * quietly tagged contacts in the background would not satisfy that. This screen IS the
 * prominent user-facing feature, which is why it says plainly what is processed, by
 * whom, and what state that processing is in — rather than hiding it behind a spinner.
 */

/** Reason codes, in words a business user can act on. */
const REASON_TEXT: Record<string, string> = {
  relationship_conflict: 'Our records and the AI disagree about who this is',
  ambiguous_message: 'The message could reasonably be read more than one way',
  low_confidence: 'The AI was not confident',
  rejected_candidate: 'The AI suggested a tag that is not approved',
  review_required_tag: 'One of these tags always needs a person to confirm it',
  not_auto_applicable_tag: 'One of these tags is not set to apply automatically',
  identity_unresolved: 'We are not certain which contact this is',
  consequential_attachment: 'An attachment changes what this message means',
  injection_suspected: 'The message contained text trying to instruct the AI',
  calibration_mode: 'This workspace asked to confirm every decision',
  would_create_contact: 'Acting on this would create a new contact',
  clear_and_approved: 'No concerns',
};

/**
 * The canonical Inbox Labels a reviewer may choose from.
 *
 * The taxonomy, not a free-text field: every downstream consumer — the attention gate, the
 * future native-label mapping — reads canonical keys, and letting somebody invent one here
 * would make the taxonomy advisory. The server checks the same list again.
 */
const INBOX_LABELS = [
  'Needs Reply', 'Follow-Up', 'Scheduling',
  'Billing / Payment', 'Support / Issue', 'Newsletter / Marketing',
] as const;

const RELATIONSHIPS = [
  'unknown', 'prospect', 'lead', 'customer', 'former_customer',
  'partner', 'vendor', 'candidate', 'internal', 'personal', 'automated_system',
] as const;

const titleCase = (s: string): string =>
  s.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

/**
 * Recent messages that finished Email Intelligence — including the ones needing nothing.
 *
 * The observation surface. The queue above it is driven by review tasks, so a message
 * that classified cleanly appeared nowhere at all: most labelled mail needs no decision,
 * and a labelling pilot that cannot see its own output is not a pilot.
 *
 * Read-only on purpose. There is no approve, no reject and no tag editor here — anything
 * that changes a CRM record stays in the queue, so this list can never be mistaken for a
 * place where something is applied to somebody's contact record.
 */
function RecentlyClassified() {
  const { data, isLoading, error } = useClassifiedMessagesQuery();
  const [viewing, setViewing] = useState<string | null>(null);

  return (
    <div>
      <SectionHeading
        title="Recently classified"
        hint="What ContactPilot read recently, including messages that needed no decision."
      />
      {isLoading ? <Spinner label="Loading recent messages…" /> : null}
      {error ? <Alert tone="error">Recent messages could not be loaded.</Alert> : null}
      {data && data.items.length === 0 ? (
        <EmptyState title="Nothing classified yet">
          Messages appear here once they have been read.
        </EmptyState>
      ) : null}
      <div className="space-y-2">
        {(data?.items ?? []).map((m) => (
          <Card key={m.intelligenceId} className="p-4">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="text-sm font-medium text-slate-900">{m.sender}</p>
              <p className="text-xs text-slate-500">{formatWhen(m.receivedAt)}</p>
            </div>
            <p className="mt-1 text-sm text-slate-700">{m.subject ?? '(no subject)'}</p>

            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
              <span className="rounded bg-slate-100 px-2 py-1 text-slate-700">
                {titleCase(m.relationship.value ?? 'unknown')}
              </span>
              {/*
                Three different facts, never collapsed: the feature was off, it was on and
                the classifier chose nothing, or it chose these. Reporting the first two
                the same way would claim an empty result where no question was asked.
              */}
              {!m.emailLabelingEnabled ? (
                <span className="text-slate-500">Labels not requested</span>
              ) : m.emailLabels.length === 0 ? (
                <span className="text-slate-500">No message labels</span>
              ) : (
                m.emailLabels.map((l) => (
                  <span
                    key={l.key}
                    className="rounded-full border border-sky-200 bg-sky-50 px-2 py-1 text-sky-900"
                  >
                    {l.key}
                  </span>
                ))
              )}
              {m.processingState === 'failed' ? (
                <span className="text-amber-700">
                  Reading failed{m.failureCode ? ` (${m.failureCode})` : ''}
                </span>
              ) : null}
              {/*
                An indicator, not a control. The decision itself belongs to the queue
                above, and duplicating an approve button here would put the same CRM
                consequence behind two different screens.
              */}
              {m.reviewTaskId ? (
                <span className="rounded bg-amber-50 px-2 py-1 text-amber-800">Review needed</span>
              ) : m.crmProposal ? (
                <span className="text-slate-500">CRM: {titleCase(m.crmProposal.operation)}</span>
              ) : (
                <span className="text-slate-500">No CRM action</span>
              )}
              {/*
                Every processed message is inspectable, not only the ones needing a
                decision. Checking the system's work on the cases it got right is how
                somebody comes to trust it enough to let it act unattended.
              */}
              {m.messageId ? (
                <button
                  type="button"
                  onClick={() => setViewing(m.messageId)}
                  className="ml-auto inline-flex min-h-11 items-center rounded px-2 py-1 text-slate-600 underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
                >
                  View message
                </button>
              ) : null}
            </div>
          </Card>
        ))}
      </div>
      {viewing ? (
        <MessageView messageId={viewing} onClose={() => setViewing(null)} />
      ) : null}
      <p className="mt-2 text-xs text-slate-500">
        Message labels describe the message inside ContactPilot. They are not CRM tags and
        are not applied to your mailbox.
      </p>
    </div>
  );
}

/**
 * What this MESSAGE is about, read-only and visibly not a CRM control.
 *
 * Read-only on purpose. Message labels are an output of a certified classifier, and the
 * only editable tag control on this screen edits a CRM proposal — putting labels in it
 * would mean "correcting" a label silently changed what gets written to Keap.
 *
 * Three states, and they are genuinely different: labelling was off for this
 * classification, labelling was on and the model chose none, and labelling was on and
 * chose some. Collapsing the first two would report an empty result where none was asked.
 */
export function EmailLabels({ labels, labelingEnabled }: {
  labels: ReadonlyArray<{ key: string; confidence: number }>;
  labelingEnabled: boolean;
}) {
  return (
    <div className="mt-4">
      <p className="text-sm text-slate-500">Message labels</p>
      {!labelingEnabled ? (
        <p className="mt-1 text-sm text-slate-500">
          Labels were not requested when this message was classified.
        </p>
      ) : labels.length === 0 ? (
        <p className="mt-1 text-sm text-slate-500">No message labels.</p>
      ) : (
        <div className="mt-2 flex flex-wrap gap-2">
          {labels.map((l) => (
            <span
              key={l.key}
              className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-sm text-sky-900"
            >
              {l.key}
            </span>
          ))}
        </div>
      )}
      <p className="mt-2 text-xs text-slate-500">
        Labels describe this message. They are not CRM tags and are not applied to your
        mailbox.
      </p>
    </div>
  );
}

/** Why the sender wrote, in words a business user reads without a glossary. */
const INTENT_TEXT: Record<string, string> = {
  buying_inquiry: 'Asking about buying from you',
  support_request: 'Needs help with something they have',
  billing: 'About a payment or an invoice',
  scheduling: 'Arranging a time with you',
  seller_outreach: 'Selling something to you',
  recruiting: 'Recruiting or job outreach',
  marketing_broadcast: 'Bulk marketing or a newsletter',
  transactional: 'Automated notification',
  general: 'General business message',
  unknown: 'Not enough to tell',
};

/**
 * The message itself, and what was concluded about it.
 *
 * Reachable from Review AND from Recently classified, because most classified mail never
 * needs a decision and "why did it label this" is worth answering about the cases the
 * system got right. That is where trust in an automatic labeller is built; the exception
 * queue is where it is spent.
 *
 * Reads what was stored at ingestion. No provider call, so nothing here needs a mailbox
 * write scope.
 */
function MessageView({ messageId, onClose }: { messageId: string; onClose: () => void }) {
  const { data, isLoading, error } = useMessageViewQuery(messageId);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const headingId = `message-view-${messageId}`;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  useEffect(() => { panelRef.current?.focus(); }, [messageId]);

  const c = data?.classification ?? null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto bg-black/30 p-0 sm:p-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        role="region"
        aria-labelledby={headingId}
        ref={panelRef}
        tabIndex={-1}
        className="mx-auto min-h-full w-full max-w-2xl bg-white p-4 shadow-xl sm:min-h-0 sm:rounded-lg sm:p-6"
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <h3 id={headingId} className="text-base font-semibold text-slate-900">Message</h3>
          <button
            type="button" onClick={onClose}
            className="rounded px-2 py-1 text-sm text-slate-600 underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
          >
            Close
          </button>
        </div>

        {isLoading ? <Spinner label="Loading the message…" /> : null}
        {error ? <Alert tone="error">This message could not be loaded.</Alert> : null}

        {data ? (
          <div className="space-y-4">
            <section>
              <p className="text-sm font-medium text-slate-900">
                {data.message.senderName ?? data.message.sender}
              </p>
              {data.message.senderName ? (
                <p className="font-mono text-xs text-slate-600">{data.message.sender}</p>
              ) : null}
              {data.message.to.length > 0 ? (
                <p className="mt-0.5 truncate text-xs text-slate-500">
                  To: {data.message.to.join(', ')}
                </p>
              ) : null}
            </section>

            <section>
              <p className="text-sm font-semibold text-slate-900">
                {data.message.subject ?? 'No subject'}
              </p>
              <p className="text-xs text-slate-500">
                {formatWhen(data.message.receivedAt)}
                {data.message.attachmentCount > 0
                  ? ` \u00b7 ${data.message.attachmentCount} attachment${data.message.attachmentCount === 1 ? '' : 's'}`
                  : ''}
              </p>
              {data.message.body ? (
                <div className="signal-message-body mt-2 max-h-80 overflow-y-auto whitespace-pre-wrap rounded-md bg-slate-50 p-3 text-sm text-slate-800">
                  {data.message.body}
                  {data.message.bodyTruncated ? (
                    <p className="mt-2 text-xs text-slate-500">
                      This message was longer than ContactPilot stores.
                    </p>
                  ) : null}
                </div>
              ) : (
                <p className="mt-2 text-sm text-slate-500">
                  No message text was stored for this message.
                </p>
              )}
              {/*
                Said plainly rather than left to be discovered. Attachments are never
                downloaded by this product, and quoted history is stripped before anything
                is stored — so neither is hidden behind a control that does not exist.
              */}
              <p className="mt-2 text-xs text-slate-500">
                Attachments are not opened, and quoted replies are not stored.
              </p>
            </section>

            {c ? (
              <section className="rounded-md border border-slate-200 p-3">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  What ContactPilot concluded
                </h4>
                <dl className="mt-2 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
                  <div>
                    <dt className="text-xs text-slate-500">Who they are</dt>
                    <dd className="text-slate-800">
                      {titleCase(c.relationship.value ?? 'unknown')}
                      {c.relationship.source ? (
                        <span className="block text-xs text-slate-500">
                          {SOURCE_TEXT[c.relationship.source] ?? titleCase(c.relationship.source)}
                        </span>
                      ) : null}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-500">Why they wrote</dt>
                    <dd className="text-slate-800">
                      {c.intent ? (INTENT_TEXT[c.intent.key] ?? titleCase(c.intent.key)) : '\u2014'}
                      {c.intent?.source === 'deterministic' ? (
                        <span className="block text-xs text-slate-500">
                          from the message headers
                        </span>
                      ) : null}
                    </dd>
                  </div>
                </dl>
                <div className="mt-3">
                  <p className="text-xs text-slate-500">Message labels</p>
                  {!c.emailLabelingEnabled ? (
                    <p className="mt-1 text-sm text-slate-500">
                      Labels were not requested when this was classified.
                    </p>
                  ) : c.emailLabels.length === 0 ? (
                    <p className="mt-1 text-sm text-slate-500">No message labels.</p>
                  ) : (
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {c.emailLabels.map((l) => (
                        <span
                          key={l.key}
                          className="rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-xs text-sky-900"
                        >
                          {l.key}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <p className="mt-3 text-xs text-slate-500">
                  CRM action:{' '}
                  {data.crmProposal
                    ? (OPERATION_TEXT[data.crmProposal.operation]
                      ?? titleCase(data.crmProposal.operation))
                    : 'No CRM action'}
                  {/*
                    Deliberately not the page-level sentence repeated. That one explains
                    the deployment once, at the top of the queue; this is the state of THIS
                    proposal, in the place somebody is looking at it.
                  */}
                  {data.crmWriteEnabled ? '' : ' \u00b7 not written'}
                </p>
              </section>
            ) : (
              <Alert tone="info">This message has not been classified.</Alert>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * How a relationship was arrived at, in words that are true.
 *
 * `crm_identity` used to render as "from your CRM records" beside a proposal to create a
 * new contact, which is a contradiction a reviewer was right to distrust. The phrase now
 * describes the SOURCE OF THE LABEL only; whether we can actually name the contact is a
 * separate line, because it is a separate question.
 */
const SOURCE_TEXT: Record<string, string> = {
  crm_identity: 'from a record we already hold',
  prior_correspondence: 'from previous correspondence',
  model_inference: 'inferred by the AI from this message',
  human_review: 'set by a reviewer',
};

/** The same reasons as REASON_TEXT, short enough for a queue row. */
const REASON_SHORT: Record<string, string> = {
  relationship_conflict: 'Conflicting records',
  ambiguous_message: 'Ambiguous message',
  low_confidence: 'Low confidence',
  rejected_candidate: 'Unapproved tag',
  review_required_tag: 'Tag needs confirming',
  not_auto_applicable_tag: 'Tag is manual only',
  identity_unresolved: 'Identity unclear',
  consequential_attachment: 'Attachment matters',
  injection_suspected: 'Suspicious content',
  calibration_mode: 'Confirmation required',
  would_create_contact: 'New contact',
};

/** What would happen in Keap, in one phrase. Never mixed with labels or relationship. */
const OPERATION_TEXT: Record<string, string> = {
  create_contact: 'Create a new contact',
  apply_tags: 'Tag the existing contact',
  update_contact: 'Update the existing contact',
  none: 'No CRM action',
};

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'create_contact', label: 'Would create a contact' },
  { id: 'identity', label: 'Identity to check' },
  { id: 'low_confidence', label: 'Low confidence' },
] as const;

type FilterId = typeof FILTERS[number]['id'];

/** A relative time for today's mail, an absolute one for anything older. */
function shortWhen(iso: string | null): string {
  if (!iso) return 'Time unavailable';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Time unavailable';
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) {
    return `Today · ${d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`;
  }
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) {
    return `Yesterday · ${d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`;
  }
  return d.toLocaleDateString(undefined, {
    year: d.getFullYear() === now.getFullYear() ? undefined : 'numeric',
    month: 'short', day: 'numeric',
  });
}

/** Two lines of identity: who sent it, and whether we can name them in the CRM. */
function IdentityNote({ item }: { item: ReviewItem }) {
  if (item.identity.basis === 'crm_mapping') {
    return (
      <p className="text-sm text-slate-700">
        Matched to an existing contact in your CRM.
      </p>
    );
  }
  if (item.identity.basis === 'local_identity') {
    return (
      <p className="text-sm text-slate-700">
        Matched to a contact ContactPilot holds, which is not linked to a CRM record yet.
      </p>
    );
  }
  return (
    <p className="text-sm text-slate-700">
      No existing contact matches this sender.
      {item.relationship.source === 'crm_identity' || item.relationship.value === 'customer' ? (
        <>
          {' '}
          The relationship below is a conclusion about the person, not a match to a
          record — ContactPilot cannot safely link this sender to a contact you already
          have.
        </>
      ) : null}
    </p>
  );
}

/** Relationship, message labels and CRM action, kept apart on purpose. */
function DimensionChips({ item }: { item: ReviewItem }) {
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
      <span className="rounded bg-slate-100 px-2 py-0.5 font-medium text-slate-700">
        {titleCase(item.relationship.value ?? 'unknown')}
        {item.relationship.confidence !== null
          ? ` · ${Math.round(item.relationship.confidence * 100)}%` : ''}
      </span>
      {item.emailLabelingEnabled && item.emailLabels.length > 0
        ? item.emailLabels.map((l) => (
          <span
            key={l.key}
            className="rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-sky-900"
          >
            {l.key}
          </span>
        ))
        : null}
      {item.proposedOperation ? (
        <span className="rounded border border-slate-300 px-2 py-0.5 text-slate-600">
          {OPERATION_TEXT[item.proposedOperation.operation] ?? titleCase(item.proposedOperation.operation)}
        </span>
      ) : null}
    </div>
  );
}

/**
 * One scannable row.
 *
 * Everything a reviewer needs to decide whether to OPEN this item, and nothing more.
 * The long explanatory copy that used to repeat on every card now lives once at the top
 * of the page and once inside the panel.
 */
function QueueRow({ item, selected, onOpen, buttonRef }: {
  item: ReviewItem;
  selected: boolean;
  onOpen: () => void;
  buttonRef?: (el: HTMLButtonElement | null) => void;
}) {
  const reason = item.primaryReason;
  return (
    <li>
      <button
        type="button"
        ref={buttonRef}
        onClick={onOpen}
        aria-current={selected ? 'true' : undefined}
        className={`w-full rounded-lg border px-4 py-3 text-left transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 ${
          selected
            ? 'border-slate-900 bg-white shadow-sm'
            : 'border-slate-200 bg-white hover:border-slate-400'
        }`}
      >
        <div className="flex items-baseline justify-between gap-3">
          <span className="min-w-0 truncate text-sm font-semibold text-slate-900">
            {item.senderName ?? item.sender}
          </span>
          <span className="shrink-0 text-xs text-slate-500">{shortWhen(item.receivedAt)}</span>
        </div>
        {item.senderName ? (
          <p className="truncate font-mono text-xs text-slate-500">{item.sender}</p>
        ) : null}
        <p className="mt-0.5 truncate text-sm text-slate-700">
          {item.subject ?? 'No subject'}
        </p>
        <DimensionChips item={item} />
        {reason ? (
          <p className="mt-1.5 text-xs font-medium text-amber-800">
            {REASON_SHORT[reason] ?? titleCase(reason)}
          </p>
        ) : null}
      </button>
    </li>
  );
}

/**
 * The deciding surface.
 *
 * Answers the four questions the queue row deliberately leaves partly open — who, what,
 * why a person is needed, and what happens on approval — and then offers the three
 * decisions. Structured evidence translated into sentences: no chain of thought, no raw
 * provider response, no Keap identifiers.
 *
 * On a small screen it is a full-screen sheet over the queue; from `lg` up it sits beside
 * the queue and stays put while the list scrolls.
 */
function ReviewDetail({ taskId, onClose, onResolved }: {
  taskId: string;
  onClose: () => void;
  onResolved: () => void;
}) {
  const { data, isLoading, error } = useReviewItemQuery(taskId);
  const { data: mappings } = useTagMappingsQuery();
  const [approve, { isLoading: approving }] = useApproveReviewMutation();
  const [reject] = useRejectReviewMutation();
  const [noAction] = useNoActionReviewMutation();
  const [editTags] = useEditReviewTagsMutation();
  const [correct] = useCorrectRelationshipMutation();
  const [saveLabels] = useSaveLabelFeedbackMutation();
  const [outcome, setOutcome] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const [viewing, setViewing] = useState(false);
  /*
   * The reviewer's working set, seeded from the classifier once the item loads.
   *
   * Local until saved, because a correction is a considered statement rather than a
   * running commentary — and every chip toggle writing evidence would turn one opinion
   * into several.
   */
  const [labelDraft, setLabelDraft] = useState<string[] | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const headingId = `review-detail-${taskId}`;

  const item = data?.item ?? null;
  const crmWriteEnabled = data?.crmWriteEnabled ?? false;
  const approvedTags = (mappings?.approvedTags ?? [])
    .filter((t) => t.aiAllowed && t.enabled && t.category !== 'email')
    .map((t) => t.canonicalKey);
  const tags = item?.crmTags ?? [];

  // Escape closes, which is what a sheet over the queue has to honour.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Opening moves focus into the panel; closing returns it to the row that opened it.
  useEffect(() => { panelRef.current?.focus(); }, [taskId]);
  // A different item is a different draft; never carry one reviewer's edit onto another.
  useEffect(() => { setLabelDraft(null); }, [taskId]);

  const act = async (fn: () => Promise<unknown>, ok: string, resolves: boolean) => {
    setOutcome(null);
    try {
      await fn();
      setOutcome({ tone: 'success', text: ok });
      if (resolves) onResolved();
    } catch (e) {
      const d = (e as { data?: { message?: string; reason?: string } }).data;
      setOutcome({
        tone: 'error',
        text: d?.reason
          ? `This needs a fresh look — ${d.message ?? d.reason}`
          : 'That could not be completed.',
      });
    }
  };

  const toggleTag = async (key: string) => {
    const next = tags.includes(key) ? tags.filter((t) => t !== key) : [...tags, key];
    await act(() => editTags({ id: taskId, tagKeys: next }).unwrap(), 'Tags updated.', false);
  };

  const op = item?.proposedOperation ?? null;
  const classified = item?.emailLabels.map((l) => l.key) ?? [];
  const labels = labelDraft ?? classified;
  const dirty = labelDraft !== null
    && (labelDraft.length !== classified.length
      || labelDraft.some((k) => !classified.includes(k)));

  return (
    <div
      className="fixed inset-0 z-40 overflow-y-auto bg-white p-4 lg:static lg:z-auto lg:rounded-lg lg:border lg:border-slate-200 lg:p-5 lg:shadow-sm"
      /*
       * A labelled region, not a dialog.
       *
       * From `lg` up this sits BESIDE the queue and the rest of the page is genuinely
       * still usable, so `aria-modal` would be false there. Below `lg` it covers the
       * queue — but there is no focus trap here, so `aria-modal` would be a promise the
       * markup does not keep either way, and a screen reader told the rest of the page is
       * inert when it is not is worse served than one told nothing.
       *
       * What a person actually needs is kept: the region is named, focus moves into it on
       * open, Escape closes it, and focus returns to the row that opened it.
       */
      role="region"
      aria-labelledby={headingId}
      ref={panelRef}
      tabIndex={-1}
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <h3 id={headingId} className="text-base font-semibold text-slate-900">
          Review this message
        </h3>
        <button
          type="button"
          onClick={onClose}
          className="rounded px-2 py-1 text-sm text-slate-600 underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
        >
          Close
        </button>
      </div>

      {isLoading ? <Spinner label="Loading this message…" /> : null}
      {error ? <Alert tone="error">This item could not be loaded.</Alert> : null}

      {item ? (
        <div className="space-y-4">
          {/* WHO */}
          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">From</h4>
            <p className="mt-1 text-sm font-medium text-slate-900">
              {item.senderName ?? item.sender}
            </p>
            {item.senderName ? (
              <p className="font-mono text-xs text-slate-600">{item.sender}</p>
            ) : null}
            <div className="mt-1"><IdentityNote item={item} /></div>
          </section>

          {/* WHAT */}
          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Message</h4>
            <p className="mt-1 text-sm font-medium text-slate-900">
              {item.subject ?? 'No subject'}
            </p>
            <p className="text-xs text-slate-500">
              {formatWhen(item.receivedAt)}
              {item.hasAttachment ? ' · has an attachment' : ''}
              {item.modelTier ? ` · ${item.modelTier === 'deep' ? 'detailed' : 'quick'} analysis` : ''}
            </p>
            {item.messageId ? (
              <button
                type="button"
                onClick={() => setViewing(true)}
                className="mt-1 inline-flex min-h-11 items-center rounded text-sm text-slate-700 underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
              >
                View message
              </button>
            ) : null}
            {data?.excerpt ? (
              <blockquote className="signal-message-body mt-2 whitespace-pre-wrap rounded-md bg-slate-50 p-3 text-sm text-slate-700">
                {data.excerpt}
                {data.excerptTruncated ? <span className="text-slate-400"> …</span> : null}
              </blockquote>
            ) : (
              <p className="mt-2 text-sm text-slate-500">
                No excerpt was stored for this message.
              </p>
            )}
          </section>

          {/* Relationship — who this person is to us */}
          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Relationship
            </h4>
            <p className="mt-1 text-xs text-slate-500">Who this person is to your business.</p>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <label className="sr-only" htmlFor={`rel-${taskId}`}>Relationship</label>
              <select
                id={`rel-${taskId}`}
                value={item.relationship.value ?? 'unknown'}
                onChange={(e) => void act(
                  () => correct({ id: taskId, relationship: e.target.value }).unwrap(),
                  'Relationship corrected. The proposed action was re-checked against it.',
                  false,
                )}
                className="rounded-md border border-slate-300 px-2 py-1 text-sm"
              >
                {RELATIONSHIPS.map((r) => <option key={r} value={r}>{titleCase(r)}</option>)}
              </select>
              {item.relationship.confidence !== null ? (
                <span className="text-xs text-slate-600">
                  {Math.round(item.relationship.confidence * 100)}% confident
                </span>
              ) : null}
            </div>
            {item.relationship.source ? (
              <p className="mt-1 text-xs text-slate-500">
                {SOURCE_TEXT[item.relationship.source] ?? titleCase(item.relationship.source)}
              </p>
            ) : null}
            {item.modelRelationship && item.modelRelationship !== item.relationship.value ? (
              <p className="mt-1 text-xs text-amber-700">
                The AI read this as {titleCase(item.modelRelationship)}.
              </p>
            ) : null}
            <p className="mt-1 text-xs text-slate-500">
              Changing this records your correction against this reading and re-checks the
              proposed action. It does not write anything to your CRM.
            </p>
          </section>

          {/* Message labels — what the message is about, and correctable */}
          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Message labels
            </h4>
            <p className="mt-1 text-xs text-slate-500">
              What this message is about and what attention it needs. Not CRM tags, and not
              applied to your mailbox.
            </p>
            {item.emailLabelingEnabled ? (
              <>
                <div className="mt-2 flex flex-wrap gap-2">
                  {INBOX_LABELS.map((key) => {
                    const on = labels.includes(key);
                    return (
                      <button
                        key={key} type="button"
                        aria-pressed={on}
                        onClick={() => setLabelDraft(
                          on ? labels.filter((k) => k !== key) : [...labels, key],
                        )}
                        className={`rounded-full border px-3 py-1 text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 ${
                          on
                            ? 'border-sky-300 bg-sky-50 text-sky-900'
                            : 'border-slate-300 bg-white text-slate-600'
                        }`}
                      >
                        {key}
                      </button>
                    );
                  })}
                </div>
                {/*
                  Saved as a considered correction, not on every toggle. Positive evidence
                  counts too: a kept label is recorded as the classifier being right, which
                  is what stops a store that only hears about mistakes concluding the
                  classifier is worse than it is.
                */}
                {dirty ? (
                  <div className="mt-2 flex items-center gap-3">
                    <Button
                      variant="secondary"
                      onClick={() => void act(
                        () => saveLabels({ id: taskId, labelKeys: labels }).unwrap(),
                        'Label correction saved. Nothing was sent to your CRM or mailbox.',
                        false,
                      )}
                    >
                      Save correction
                    </Button>
                    <button
                      type="button"
                      onClick={() => setLabelDraft(null)}
                      className="text-sm text-slate-600 underline"
                    >
                      Reset
                    </button>
                  </div>
                ) : null}
              </>
            ) : (
              <p className="mt-2 text-sm text-slate-500">
                Labels were not requested when this message was classified.
              </p>
            )}
          </section>

          {/* CRM action — what would happen in Keap */}
          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              CRM action
            </h4>
            <p className="mt-1 text-sm text-slate-800">
              {op ? (OPERATION_TEXT[op.operation] ?? titleCase(op.operation)) : 'No CRM action'}
              {op && tags.length > 0 ? ` with ${tags.join(', ')}` : ''}
            </p>
            {op && op.operation !== 'none' ? (
              <>
                <p className="mt-2 text-xs text-slate-500">CRM tags to apply to the contact</p>
                <div className="mt-1 flex flex-wrap gap-2">
                  {approvedTags.map((key) => (
                    <button
                      key={key} type="button" onClick={() => void toggleTag(key)}
                      aria-pressed={tags.includes(key)}
                      className={`rounded-full border px-3 py-1 text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 ${
                        tags.includes(key)
                          ? 'border-slate-900 bg-slate-900 text-white'
                          : 'border-slate-300 bg-white text-slate-700'
                      }`}
                    >
                      {key}
                    </button>
                  ))}
                </div>
              </>
            ) : null}
            {item.rejectedTags.length > 0 ? (
              <p className="mt-2 text-xs text-amber-700">
                The AI also suggested {item.rejectedTags.map((r) => r.key).join(', ')}, which
                {item.rejectedTags.length === 1 ? ' is' : ' are'} not approved for automatic use.
              </p>
            ) : null}
          </section>

          {/* WHY a person is needed */}
          <section className="rounded-md bg-slate-50 p-3">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Why this needs you
            </h4>
            <ul className="mt-1 list-inside list-disc text-sm text-slate-700">
              {item.reviewReasons.map((r) => <li key={r}>{REASON_TEXT[r] ?? titleCase(r)}</li>)}
            </ul>
          </section>

          {data?.stale ? (
            <Alert tone="error">
              Something changed since this was proposed, so it needs a fresh look.
            </Alert>
          ) : null}

          {outcome ? <Alert tone={outcome.tone}>{outcome.text}</Alert> : null}

          {/*
            Three decisions with three different meanings, said in the UI rather than left
            for a reviewer to infer from the button labels.
          */}
          <section>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => void act(
                  () => approve({ id: taskId }).unwrap(),
                  crmWriteEnabled
                    ? 'Approved.'
                    : 'Approved. Your decision is recorded; nothing was sent to Keap.',
                  true,
                )}
                disabled={approving || !op || op.operation === 'none'}
              >
                Approve
              </Button>
              <Button
                variant="secondary"
                onClick={() => void act(
                  () => reject({ id: taskId }).unwrap(), 'Rejected.', true,
                )}
              >
                Reject
              </Button>
              <Button
                variant="secondary"
                onClick={() => void act(
                  () => noAction({ id: taskId }).unwrap(), 'Closed with no action.', true,
                )}
              >
                No action
              </Button>
            </div>
            <dl className="mt-2 space-y-0.5 text-xs text-slate-500">
              <div><dt className="inline font-medium">Approve</dt>
                <dd className="inline"> — the reading and the proposed CRM action are right.</dd></div>
              <div><dt className="inline font-medium">Reject</dt>
                <dd className="inline"> — the reading or the proposal is wrong.</dd></div>
              <div><dt className="inline font-medium">No action</dt>
                <dd className="inline"> — the reading is fine, but nothing should happen in the CRM.</dd></div>
            </dl>
          </section>
        </div>
      ) : null}
      {viewing && item?.messageId ? (
        <MessageView messageId={item.messageId} onClose={() => setViewing(false)} />
      ) : null}
    </div>
  );
}

/**
 * One page of the queue.
 *
 * Each page is its own cached query keyed by its cursor, so "load more" adds a page
 * rather than refetching and re-rendering everything before it. The DOM stays bounded by
 * how many pages a person actually asked for.
 */
function QueuePage({ cursor, filter, index, selectedId, onOpen, onNext, registerRef }: {
  cursor: string | null;
  filter: FilterId;
  index: number;
  selectedId: string | null;
  onOpen: (id: string) => void;
  onNext: (index: number, next: string | null, count: number) => void;
  registerRef: (id: string, el: HTMLButtonElement | null) => void;
}) {
  const { data, isLoading, error } = useReviewQueueQuery({
    filter, ...(cursor ? { before: cursor } : {}),
  });

  useEffect(() => {
    if (data) onNext(index, data.nextBefore, data.items.length);
  }, [data, index, onNext]);

  if (isLoading) return <Spinner label="Loading the queue…" />;
  if (error) return <Alert tone="error">The review queue could not be loaded.</Alert>;

  return (
    <>
      {(data?.items ?? []).map((item) => (
        <QueueRow
          key={item.id}
          item={item}
          selected={item.id === selectedId}
          onOpen={() => onOpen(item.id)}
          buttonRef={(el) => registerRef(item.id, el)}
        />
      ))}
    </>
  );
}

/**
 * The message queue from before email capture.
 *
 * A compact queue, and a panel for the one item somebody is actually looking at. It used
 * to be a hundred fully-expanded cards in a single column, each repeating the same three
 * paragraphs of explanation — so the page said the same thing a hundred times and the
 * reviewer's own work was the part that scrolled off screen. The persistent copy is now
 * stated once here, and the per-item context lives in the panel where a decision is made.
 *
 * Kept, not deleted: it is history somebody may still need to look up, and the decisions
 * in it were real. It is mounted only inside Review's collapsed "Archived messages"
 * disclosure, under the All filter, so it never competes with today's work for attention.
 *
 * No automation-mode card and no mode name. Those described how the retired classifier
 * was being rolled out — an operator's fact, not something a customer can act on.
 */
export function ArchivedMessages() {
  const [filter, setFilter] = useState<FilterId>('all');
  const [cursors, setCursors] = useState<Array<string | null>>([null]);
  const [tail, setTail] = useState<string | null>(null);
  const [firstPageCount, setFirstPageCount] = useState<number | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const rowRefs = useRef(new Map<string, HTMLButtonElement | null>());
  /*
   * Read inside a stable callback rather than closed over.
   *
   * `onNext` is a dependency of the page's effect, so a callback that changed whenever
   * `cursors` did would re-run every page's effect on every page turn.
   */
  const cursorsRef = useRef(cursors);
  cursorsRef.current = cursors;

  const registerRef = useCallback((id: string, el: HTMLButtonElement | null) => {
    if (el) rowRefs.current.set(id, el); else rowRefs.current.delete(id);
  }, []);

  const onNext = useCallback((index: number, next: string | null, count: number) => {
    // Only the LAST page can offer another one; an earlier page's cursor is spent.
    if (index === cursorsRef.current.length - 1) setTail(next);
    if (index === 0) setFirstPageCount(count);
  }, []);

  // A filter change is a different queue, so the pages and the open item both reset.
  const changeFilter = (next: FilterId) => {
    setFilter(next);
    setCursors([null]);
    setTail(null);
    setSelectedId(null);
  };

  /** Closing returns focus to the row that opened the panel. */
  const closeDetail = useCallback(() => {
    const id = selectedId;
    setSelectedId(null);
    if (id) requestAnimationFrame(() => rowRefs.current.get(id)?.focus());
  }, [selectedId]);

  return (
    <div className="space-y-6">
      <div>
        <SectionHeading
          title="Messages needing review"
          hint="Archived messages still waiting for a decision."
        />

        {/*
          Said once, at the top, instead of on every row.
        */}
        <div className="mb-3 space-y-1 text-sm text-slate-600">
          <p>
            Approving records your decision. CRM writing is switched off for these
            messages, so nothing is sent to your CRM from here.
          </p>
        </div>

        <div className="mb-3 flex flex-wrap gap-2" role="group" aria-label="Filter archived messages">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => changeFilter(f.id)}
              aria-pressed={filter === f.id}
              className={`rounded-full border px-3 py-1 text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 ${
                filter === f.id
                  ? 'border-slate-900 bg-slate-900 text-white'
                  : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/*
          `min-w-0` on the children, and it is load-bearing rather than defensive.

          A grid item's automatic minimum size is its MIN-CONTENT size, and `truncate`
          sets `white-space: nowrap`, so a hundred-character sender address has a
          min-content width of a hundred characters. Below `lg` this grid has one implicit
          `minmax(auto, 1fr)` column with nothing to stop that, and the whole Review page
          grew to 1078px inside a 768px tablet — every row, the filters and the page
          itself scrolling sideways because one person had a long email address. The
          explicit `minmax(0, …)` above does the same job at `lg` and upward; this is the
          same fix for the single-column case.
        */}
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,26rem)] lg:items-start">
          <div className="min-w-0">
            {firstPageCount === 0 && cursors.length === 1 ? (
              <EmptyState title="Nothing needs your review right now">
                {filter === 'all'
                  ? 'Messages that need a decision will appear here.'
                  : 'No items match this filter.'}
              </EmptyState>
            ) : null}
            <ul className="space-y-2" data-legacy-queue="">
              {cursors.map((c, i) => (
                <QueuePage
                  key={c ?? 'first'}
                  cursor={c}
                  filter={filter}
                  index={i}
                  selectedId={selectedId}
                  onOpen={setSelectedId}
                  onNext={onNext}
                  registerRef={registerRef}
                />
              ))}
            </ul>
            {tail ? (
              <div className="mt-3">
                <Button
                  variant="secondary"
                  onClick={() => { setCursors((cs) => [...cs, tail]); setTail(null); }}
                >
                  Load more
                </Button>
              </div>
            ) : null}
          </div>

          {selectedId ? (
            <div className="min-w-0 lg:sticky lg:top-4">
              <ReviewDetail
                taskId={selectedId}
                onClose={closeDetail}
                onResolved={closeDetail}
              />
            </div>
          ) : (
            <div className="hidden rounded-lg border border-dashed border-slate-300 p-5 text-sm text-slate-500 lg:block">
              Select a message to see the full context and decide.
            </div>
          )}
        </div>
      </div>

      <RecentlyClassified />
    </div>
  );
}

/**
 * Whether there is any history to show at all.
 *
 * One row of each is enough to know. The disclosure is rendered only when one of them has
 * something in it: a "History" heading over nothing is a placeholder, and the old queue's
 * own pages are only fetched once somebody opens it.
 */
export function useHasHistory(): boolean {
  const queue = useReviewQueueQuery({ limit: 1 });
  const classified = useClassifiedMessagesQuery({ limit: 1 });
  return (queue.data?.items.length ?? 0) > 0 || (classified.data?.items.length ?? 0) > 0;
}
